import { atom, type Getter, type Setter } from "jotai";
import * as Sentry from "@sentry/nextjs";
import { selectedNodeIdAtom, isEditorReadOnlyAtom } from "../atoms";
import { historyStackAtom, pendingFormFlushAtom } from "../history/atoms";
import { applyTransaction } from "../history/applyTransaction";
import { closeCollapseWindow } from "../history/recordTransaction";

/**
 * Best-effort prelude for undo/redo:
 *   1. Flush any in-flight form keystrokes (so they're committed to the atom
 *      before we pop the stack).
 *   2. Seal the active typing-collapse window so the next recorded edit
 *      starts a fresh entry.
 *   3. Close the config drawer (clear the selected node) — the user wants
 *      undo/redo to be a "step back / step forward" gesture independent of
 *      whatever node was being inspected when the action was originally
 *      recorded.
 *
 * Wrapped in try/catch — an error here must not prevent the subsequent
 * undo/redo apply.
 */
function flushAndClose(get: Getter, set: Setter) {
  try {
    const flush = get(pendingFormFlushAtom);
    if (flush) flush();
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "flush-pending-form" },
    });
  }
  try {
    closeCollapseWindow(set, get);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "close-collapse-from-undo" },
    });
  }
  try {
    if (get(selectedNodeIdAtom) !== null) set(selectedNodeIdAtom, null);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "close-config-drawer" },
    });
  }
}

/**
 * Seal the active typing-collapse window so the next recorded transaction
 * starts a fresh history entry. Use after committing an edit "session"
 * (e.g. on blur of a sticky-note editor) so subsequent edits don't merge.
 */
export const closeCollapseAtom = atom(null, (get, set) => {
  closeCollapseWindow(set, get);
});

/** Pop the top of the undo stack, apply its inverse, push to redo. */
export const undoAtom = atom(null, (get, set) => {
  if (get(isEditorReadOnlyAtom)) return;
  try {
    flushAndClose(get, set);
    const stack = get(historyStackAtom);
    const top = stack.undo.at(-1);
    if (!top) return;
    applyTransaction(get, set, top, "undo");
    set(historyStackAtom, {
      undo: stack.undo.slice(0, -1),
      redo: [...stack.redo, top],
    });
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "undo" },
    });
  }
});

/** Pop the top of the redo stack, re-apply, push back to undo. */
export const redoAtom = atom(null, (get, set) => {
  if (get(isEditorReadOnlyAtom)) return;
  try {
    flushAndClose(get, set);
    const stack = get(historyStackAtom);
    const top = stack.redo.at(-1);
    if (!top) return;
    applyTransaction(get, set, top, "redo");
    set(historyStackAtom, {
      undo: [...stack.undo, top],
      redo: stack.redo.slice(0, -1),
    });
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "redo" },
    });
  }
});

/**
 * Debug helper for the history popover. Pops and applies `count` undo entries
 * one by one. Bounded by stack length, so passing a number larger than
 * `undo.length` simply empties the undo stack.
 */
export const undoToIndexAtom = atom(null, (get, set, count: number) => {
  if (get(isEditorReadOnlyAtom)) return;
  try {
    flushAndClose(get, set);
    for (let i = 0; i < count; i++) {
      const stack = get(historyStackAtom);
      const top = stack.undo.at(-1);
      if (!top) break;
      applyTransaction(get, set, top, "undo");
      set(historyStackAtom, {
        undo: stack.undo.slice(0, -1),
        redo: [...stack.redo, top],
      });
    }
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "undo-to-index", count },
    });
  }
});

export const redoToIndexAtom = atom(null, (get, set, count: number) => {
  if (get(isEditorReadOnlyAtom)) return;
  try {
    flushAndClose(get, set);
    for (let i = 0; i < count; i++) {
      const stack = get(historyStackAtom);
      const top = stack.redo.at(-1);
      if (!top) break;
      applyTransaction(get, set, top, "redo");
      set(historyStackAtom, {
        undo: [...stack.undo, top],
        redo: stack.redo.slice(0, -1),
      });
    }
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "redo-to-index", count },
    });
  }
});
